import type { QueueItem as QueueItemData, OutputFormat } from '@/lib/queue/store';
import type { QueueStore } from '@/lib/queue/store';
import type { QueueProcessor } from '@/lib/queue/processor';
import { formatBytes } from '@/lib/utils/format-bytes';
import { settingsDiffer } from '@/lib/utils/settings-differ';
import { extForOutput, detectInputFormat } from '@/lib/utils/mime';

export { settingsDiffer };

const FORMAT_FACTOR: Record<OutputFormat, number> = {
  auto: 0.55,
  jpeg: 0.55,
  webp: 0.45,
  avif: 0.25,
  png: 0.70,
  gif: 1.00,
  'gif-animated': 1.50,
  'webp-animated': 0.80,
  'mp4': 0.10, // H.264 inter-frame compression — typically 5–20× smaller than the source.
};

/** Predict the output filename for a conversion that hasn't run yet.
 *  When format='auto' the real ext is decided at convert time, so we leave the
 *  source extension in place and let the result.outName overwrite once done. */
function predictOutName(sourceName: string, format: OutputFormat): string {
  const dotIdx = sourceName.lastIndexOf('.');
  const base = dotIdx >= 0 ? sourceName.slice(0, dotIdx) : sourceName;
  if (format === 'auto') return sourceName;
  return `${base}.${extForOutput(format)}`;
}

function estimatedOutputSize(item: QueueItemData): number | null {
  if (!item.originalDimensions) return null;
  if (item.status !== 'waiting') return null;
  const factor = FORMAT_FACTOR[item.settings.format] ?? 0.55;
  const q = item.settings.quality / 100;
  return Math.round(item.file.size * q * factor);
}

const STATUS_LABELS: Record<string, string> = {
  waiting: 'Waiting',
  processing: 'Processing',
  done: 'Done',
  error: 'Error',
  cancelled: 'Cancelled',
};

/** Estimate upscale duration from image dimensions.
 *  Baseline: ~65s per 256×256 tile on single-threaded WASM — the self-correcting
 *  logic in the render path replaces this with the observed rate once inference
 *  is ≥10% in, so this is just the first guess before any real data. */
function estimateUpscaleTotalMs(item: QueueItemData): number {
  const dims = item.originalDimensions;
  if (!dims) return 65_000;
  const step = 224; // 256 tile minus 32 overlap
  const tiles = Math.max(1, Math.ceil(dims.width / step)) * Math.max(1, Math.ceil(dims.height / step));
  return tiles * 65_000;
}

function formatDuration(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r === 0 ? `${m}m` : `${m}m ${r}s`;
}

/** Tracks which items have their compare panel open. Exported so FileQueue
 *  can transfer the open state to a new conversion when one finishes (so
 *  clicking Convert while comparing auto-shows the new output). */
const compareOpen = new Map<string, boolean>();

export function isCompareOpen(id: string): boolean {
  return compareOpen.get(id) === true;
}

/** Force the compare-open state. Used by FileQueue's auto-follow logic. */
export function setCompareOpenState(id: string, open: boolean): void {
  compareOpen.set(id, open);
}

/** Blob URLs for thumbnails, keyed by item id. Created on first render and
 *  reused across re-renders so rapid store updates (e.g. progress ticks)
 *  don't churn URLs and cause net::ERR_FILE_NOT_FOUND on in-flight loads. */
interface ThumbCacheEntry {
  /** Blob URL for the source — always created on first render. */
  sourceUrl: string;
  /** Identity check: the blob the sourceUrl was built from. Lets us regenerate
   *  the URL when the displayable source changes — e.g. a HEIC item's JPEG
   *  previewBlob lands after the first render replaced the raw (unrenderable)
   *  file. */
  sourceBlob: Blob;
  /** Blob URL for the conversion result — created lazily once result lands. */
  resultUrl?: string;
  /** Identity check: result blob whose URL is cached. Lets us regenerate
   *  the URL if the result changes (e.g. retry produces a new blob). */
  resultBlob?: Blob;
}
const thumbUrlCache = new Map<string, ThumbCacheEntry>();

/** The blob a source thumbnail should render. Prefers a decoded preview
 *  (e.g. HEIC → JPEG) when present, since browsers can't render HEIC directly. */
function sourceBlobFor(item: QueueItemData): Blob {
  return item.previewBlob ?? item.file;
}

/** Get (creating/refreshing as needed) the cache entry's source blob URL,
 *  regenerating it if the displayable source blob has changed. */
function ensureSourceUrl(item: QueueItemData): ThumbCacheEntry {
  const srcBlob = sourceBlobFor(item);
  let entry = thumbUrlCache.get(item.id);
  if (!entry) {
    entry = { sourceUrl: URL.createObjectURL(srcBlob), sourceBlob: srcBlob };
    thumbUrlCache.set(item.id, entry);
  } else if (entry.sourceBlob !== srcBlob) {
    URL.revokeObjectURL(entry.sourceUrl);
    entry.sourceUrl = URL.createObjectURL(srcBlob);
    entry.sourceBlob = srcBlob;
  }
  return entry;
}

/** Returns the URL that should populate the queue-item thumbnail and the
 *  Compare panel's "after" image. For source rows and unfinished conversions
 *  this is the source file (or its decoded preview). For DONE conversions it's
 *  the result blob, so the child thumbnail visually reflects what was produced. */
function getOrCreateThumbUrl(item: QueueItemData): string {
  const entry = ensureSourceUrl(item);
  if (item.status === 'done' && item.result && !item.isSource) {
    if (entry.resultBlob !== item.result.blob) {
      if (entry.resultUrl) URL.revokeObjectURL(entry.resultUrl);
      entry.resultUrl = URL.createObjectURL(item.result.blob);
      entry.resultBlob = item.result.blob;
    }
    return entry.resultUrl!;
  }
  return entry.sourceUrl;
}

/** Always returns the source blob URL (used by the Compare panel's "before"
 *  side, which must show the unmodified original — or its renderable preview). */
function getSourceThumbUrl(item: QueueItemData): string {
  return ensureSourceUrl(item).sourceUrl;
}

/** Called by FileQueue when an item is removed from the store — revokes its
 *  cached blob URL and drops companion state. */
export function disposeQueueItem(itemId: string): void {
  const entry = thumbUrlCache.get(itemId);
  if (entry) {
    URL.revokeObjectURL(entry.sourceUrl);
    if (entry.resultUrl) URL.revokeObjectURL(entry.resultUrl);
    thumbUrlCache.delete(itemId);
  }
  compareOpen.delete(itemId);
}

/** Integer percent saved; negative if larger. */
function savedPct(originalSize: number, outSize: number): number {
  if (originalSize === 0) return 0;
  return Math.round(((originalSize - outSize) / originalSize) * 100);
}

export function createQueueItemEl(
  item: QueueItemData,
  store: QueueStore,
  processor: QueueProcessor
): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'queue-item-wrapper';
  wrapper.dataset.id = item.id;

  const el = document.createElement('div');
  el.className = `queue-item queue-item--${item.status}`;

  // Thumb shows the result (post-filter / post-encode) when the conversion
  // is done so the queue UI accurately reflects each child's output. Source
  // rows and unfinished children fall back to the original blob.
  const thumbIsVideo = !item.isSource && item.status === 'done'
    && item.result?.blob.type.startsWith('video/') === true;
  // HEIC can't be rendered by <img>, so the queue shows a decoded JPEG preview
  // (previewBlob) that lands a moment after upload. Until it's ready — and as
  // long as we're not already showing a renderable result — show a spinner
  // instead of a broken image. (heic-preview.ts writes previewBlob async.)
  const heicAwaitingPreview =
    item.previewBlob === undefined &&
    detectInputFormat(item.file) === 'heic' &&
    !(item.status === 'done' && item.result && !item.isSource);
  let thumb: HTMLElement;
  if (heicAwaitingPreview) {
    const ph = document.createElement('div');
    ph.className = 'queue-item__thumb queue-item__thumb--loading';
    ph.setAttribute('role', 'img');
    ph.setAttribute('aria-label', `Generating preview for ${item.file.name}`);
    ph.title = 'Generating preview…';
    const spinner = document.createElement('div');
    spinner.className = 'queue-item__thumb-spinner';
    ph.appendChild(spinner);
    thumb = ph;
  } else if (thumbIsVideo) {
    const thumbUrl = getOrCreateThumbUrl(item);
    const v = document.createElement('video');
    v.className = 'queue-item__thumb';
    v.src = thumbUrl;
    v.muted = true;
    v.loop = true;
    v.autoplay = true;
    v.playsInline = true;
    v.setAttribute('aria-label', item.file.name);
    thumb = v;
  } else {
    const thumbUrl = getOrCreateThumbUrl(item);
    const img = document.createElement('img');
    img.className = 'queue-item__thumb';
    img.alt = item.file.name;
    img.src = thumbUrl;
    thumb = img;
  }
  // Compare's "before" pane always uses the unmodified source, regardless of
  // whether this row is a source or a done conversion.
  const originalUrl = getSourceThumbUrl(item);

  const info = document.createElement('div');
  info.className = 'queue-item__info';

  const name = document.createElement('span');
  name.className = 'queue-item__name';
  // Source rows: original filename. Conversion children: predicted/actual output name.
  const displayName = !item.isSource
    ? (item.result?.outName ?? predictOutName(item.file.name, item.settings.format))
    : item.file.name;
  name.textContent = displayName;
  name.title = displayName;

  const meta = document.createElement('span');
  meta.className = 'queue-item__meta';

  if (item.status === 'done' && item.result) {
    meta.textContent = `${formatBytes(item.file.size)} → ${formatBytes(item.result.outSize)}`;
  } else {
    const estSize = estimatedOutputSize(item);
    const estText = estSize !== null ? ` · ≈ ${formatBytes(estSize)} expected` : '';
    meta.textContent = formatBytes(item.file.size) + estText;
  }

  info.appendChild(name);
  info.appendChild(meta);

  const progressBar = document.createElement('div');
  progressBar.className = 'queue-item__progress-bar';
  progressBar.style.display = item.status === 'processing' ? '' : 'none';
  progressBar.setAttribute('role', 'progressbar');
  progressBar.setAttribute('aria-valuemin', '0');
  progressBar.setAttribute('aria-valuemax', '100');
  progressBar.setAttribute('aria-valuenow', String(item.progress));
  progressBar.setAttribute('aria-label', `Conversion progress for ${item.file.name}`);

  const progressFill = document.createElement('div');
  progressFill.className = 'queue-item__progress-fill';
  progressFill.style.width = `${item.progress}%`;
  progressBar.appendChild(progressFill);
  info.appendChild(progressBar);

  // Dedicated upscale progress bar + ETA while inference is in flight.
  if (item.status === 'processing' && item.upscaleStartedAt) {
    const upscaleWrap = document.createElement('div');
    upscaleWrap.className = 'queue-item__upscale-row';

    const upBar = document.createElement('div');
    upBar.className = 'queue-item__upscale-bar';
    const upFill = document.createElement('div');
    upFill.className = 'queue-item__upscale-fill';
    upBar.appendChild(upFill);

    const etaText = document.createElement('span');
    etaText.className = 'queue-item__upscale-eta';

    upscaleWrap.appendChild(upBar);
    upscaleWrap.appendChild(etaText);
    info.appendChild(upscaleWrap);

    const startedAt = item.upscaleStartedAt;
    function refreshEta(): void {
      const elapsed = Date.now() - startedAt;
      const pctPhase = Math.max(0, Math.min(1, (item.progress - 5) / 65));
      const baselineTotal = estimateUpscaleTotalMs(item);
      // Once ≥10% through, trust the actual rate; otherwise use the baseline.
      const estTotal = pctPhase > 0.1 ? elapsed / pctPhase : baselineTotal;
      const remaining = Math.max(0, estTotal - elapsed);
      const pctDisplay = Math.min(100, Math.round((elapsed / estTotal) * 100));
      upFill.style.width = `${pctDisplay}%`;
      etaText.textContent =
        remaining < 1000
          ? `↑ Finalising upscale…`
          : `↑ Upscaling · ~${formatDuration(remaining)} remaining · ${formatDuration(elapsed)} elapsed`;
      upBar.setAttribute('aria-valuenow', String(pctDisplay));
    }
    refreshEta();

    // Tick every second so the countdown feels alive between progress events.
    const tickId = window.setInterval(refreshEta, 1000);
    // Clear when the wrapper is dropped from the DOM by the next FileQueue render.
    const obs = new MutationObserver(() => {
      if (!document.contains(wrapper)) {
        clearInterval(tickId);
        obs.disconnect();
      }
    });
    obs.observe(document.body, { childList: true, subtree: true });
  }

  if (item.error) {
    const errMsg = document.createElement('span');
    errMsg.className = 'queue-item__error';
    errMsg.textContent = item.error;
    info.appendChild(errMsg);
  }

  // Prominent saved-% bubble — shown only when done and we have a meaningful change
  let savedBubble: HTMLElement | null = null;
  if (item.status === 'done' && item.result) {
    const pct = savedPct(item.file.size, item.result.outSize);
    savedBubble = document.createElement('span');
    const polarity = pct > 0 ? 'saved' : pct < 0 ? 'larger' : 'same';
    savedBubble.className = `queue-item__saved queue-item__saved--${polarity}`;
    if (pct > 0) savedBubble.textContent = `−${pct}%`;
    else if (pct < 0) savedBubble.textContent = `+${Math.abs(pct)}%`;
    else savedBubble.textContent = '±0%';
    savedBubble.title = pct > 0 ? `${pct}% smaller than original`
      : pct < 0 ? `${Math.abs(pct)}% larger than original`
      : 'Same size as original';
  }

  // Upscaled bubble — shown when AI upscaling ran
  let upscaledBubble: HTMLElement | null = null;
  if (item.upscaledBy) {
    upscaledBubble = document.createElement('span');
    upscaledBubble.className = 'queue-item__upscaled';
    upscaledBubble.textContent = `↑ Upscaled ${item.upscaledBy}×`;
    upscaledBubble.title = `AI upscaling applied (${item.upscaledBy}× super-resolution)`;
  }

  const badge = document.createElement('span');
  badge.className = `queue-item__badge queue-item__badge--${item.status}`;
  badge.textContent = STATUS_LABELS[item.status] ?? item.status;

  // Actions
  const actions = document.createElement('div');
  actions.className = 'queue-item__actions';

  if (item.status === 'waiting' || item.status === 'processing') {
    const cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.className = 'queue-item__cancel-btn';
    cancelBtn.setAttribute('aria-label', `Cancel ${item.file.name}`);
    cancelBtn.textContent = 'Cancel';
    cancelBtn.addEventListener('click', () => processor.cancelItem(item.id));
    actions.appendChild(cancelBtn);
  }

  if (item.status === 'error' || item.status === 'cancelled') {
    const retryBtn = document.createElement('button');
    retryBtn.type = 'button';
    retryBtn.className = 'queue-item__retry-btn';
    retryBtn.setAttribute('aria-label', `Retry ${item.file.name}`);
    retryBtn.textContent = 'Retry';
    retryBtn.addEventListener('click', () => processor.retryItem(item.id));
    actions.appendChild(retryBtn);
  }

  // Compare panel (lazy) + Compare + Download buttons — only when done
  let comparePanel: HTMLElement | null = null;
  let compareBtn: HTMLButtonElement | null = null;

  const comparePanelId = `compare-panel-${item.id}`;

  function setCompareOpen(open: boolean): void {
    compareOpen.set(item.id, open);
    if (!compareBtn) return;
    compareBtn.setAttribute('aria-expanded', String(open));
    compareBtn.textContent = open ? 'Hide compare' : 'Compare';

    if (open) {
      if (!comparePanel && item.result) {
        comparePanel = buildComparePanel(comparePanelId, originalUrl, item.result.blob);
        wrapper.appendChild(comparePanel);
      }
      if (comparePanel) comparePanel.style.display = '';
    } else if (comparePanel) {
      comparePanel.style.display = 'none';
    }
  }

  // Source rows render no Compare/Download/Reconvert. Conversion children
  // get Compare + Download. Re-convert is removed; the new Convert button
  // in the settings card is the single mechanism to add new conversions.
  const isSource = !!item.isSource;
  if (!isSource && item.status === 'done' && item.result) {
    compareBtn = document.createElement('button');
    compareBtn.type = 'button';
    compareBtn.className = 'queue-item__compare-btn';
    compareBtn.setAttribute('aria-controls', comparePanelId);
    compareBtn.setAttribute('aria-expanded', 'false');
    compareBtn.textContent = 'Compare';
    compareBtn.addEventListener('click', () => {
      const current = compareOpen.get(item.id) ?? false;
      setCompareOpen(!current);
    });
    actions.appendChild(compareBtn);

    const editBtn = document.createElement('button');
    editBtn.type = 'button';
    // Reuse compare-btn styling — Edit is a secondary action like Compare,
    // not a primary one like Download.
    editBtn.className = 'queue-item__compare-btn queue-item__edit-btn';
    editBtn.setAttribute('aria-label', `Edit ${item.result.outName} as a new input`);
    editBtn.title = 'Add this output as a new input image';
    editBtn.textContent = 'Edit';
    editBtn.addEventListener('click', () => {
      store.promoteOutputToSource(item.id);
    });
    actions.appendChild(editBtn);

    const dlBtn = document.createElement('button');
    dlBtn.type = 'button';
    dlBtn.className = 'queue-item__download-btn';
    dlBtn.setAttribute('aria-label', `Download ${item.result.outName}`);
    dlBtn.textContent = 'Download';
    dlBtn.addEventListener('click', () => {
      if (!item.result) return;
      const url = URL.createObjectURL(item.result.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = item.result.outName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    });
    actions.appendChild(dlBtn);
  }

  const removeBtn = document.createElement('button');
  removeBtn.className = 'queue-item__remove';
  removeBtn.type = 'button';
  removeBtn.setAttribute('aria-label', `Remove ${item.file.name}`);
  removeBtn.textContent = '×';
  removeBtn.addEventListener('click', () => {
    // The item's blob URL and companion state are revoked by FileQueue when
    // it notices the item has dropped out of the store.
    store.removeFile(item.id);
  });

  el.appendChild(thumb);
  el.appendChild(info);
  if (!isSource && savedBubble) el.appendChild(savedBubble);
  if (!isSource && upscaledBubble) el.appendChild(upscaledBubble);
  // Badge: hidden on sources, and hidden on done conversions (the Download
  // button is the indicator that the conversion is complete). Still shown
  // for processing/error/cancelled/waiting so the user can see in-flight state.
  if (!isSource && item.status !== 'done') el.appendChild(badge);
  el.appendChild(actions);
  el.appendChild(removeBtn);

  wrapper.appendChild(el);

  // Restore compare-panel open state across re-renders
  if (compareOpen.get(item.id)) setCompareOpen(true);

  return wrapper;
}

function makeMediaElement(url: string, blobType: string, className: string, alt: string): HTMLImageElement | HTMLVideoElement {
  if (blobType.startsWith('video/')) {
    const v = document.createElement('video');
    v.className = className;
    v.src = url;
    v.muted = true;
    v.loop = true;
    v.autoplay = true;
    v.playsInline = true;
    v.setAttribute('aria-label', alt);
    return v;
  }
  const img = document.createElement('img');
  img.className = className;
  img.alt = alt;
  img.src = url;
  return img;
}

/** Before/after slider comparison panel. `before` is the original Blob URL; `after` is the result Blob. */
function buildComparePanel(panelId: string, beforeUrl: string, afterBlob: Blob): HTMLElement {
  const panel = document.createElement('div');
  panel.className = 'compare-panel';
  panel.id = panelId;

  // Prevent drag gestures inside the compare panel from bubbling up to the
  // document-level drop handler (which would try to add files).
  panel.addEventListener('dragstart', (e) => {
    e.preventDefault();
    e.stopPropagation();
  });
  panel.addEventListener('pointerdown', (e) => {
    e.stopPropagation();
  });

  const viewport = document.createElement('div');
  viewport.className = 'compare-panel__viewport';

  const afterUrl = URL.createObjectURL(afterBlob);

  // For MP4 / WebM the after side must be a <video>; <img> can't render video.
  const afterImg = makeMediaElement(afterUrl, afterBlob.type, 'compare-panel__after', 'Converted result');
  afterImg.draggable = false;

  const beforeClip = document.createElement('div');
  beforeClip.className = 'compare-panel__before-clip';

  // The "before" side could itself be a video later, but for now sources are
  // images; keep this as <img>.
  const beforeImg = document.createElement('img');
  beforeImg.className = 'compare-panel__before';
  beforeImg.alt = 'Original';
  beforeImg.src = beforeUrl;
  beforeImg.draggable = false;
  beforeClip.appendChild(beforeImg);

  const handle = document.createElement('div');
  handle.className = 'compare-panel__handle';

  viewport.appendChild(afterImg);
  viewport.appendChild(beforeClip);
  viewport.appendChild(handle);

  viewport.style.setProperty('--split', '50%');

  const labels = document.createElement('div');
  labels.className = 'compare-panel__labels';
  labels.innerHTML = `<span class="compare-panel__label">Original</span><span class="compare-panel__label">Converted</span>`;

  const slider = document.createElement('input');
  slider.type = 'range';
  slider.min = '0';
  slider.max = '100';
  slider.value = '50';
  slider.className = 'compare-panel__slider';
  slider.setAttribute('aria-label', 'Compare slider: left is original, right is converted');

  function setSplit(v: number): void {
    const pct = Math.max(0, Math.min(100, v));
    viewport.style.setProperty('--split', `${pct}%`);
  }
  slider.addEventListener('input', () => setSplit(Number(slider.value)));

  // Drag on the viewport for more direct interaction
  let dragging = false;
  function handlePointer(e: PointerEvent): void {
    const rect = viewport.getBoundingClientRect();
    const pct = ((e.clientX - rect.left) / rect.width) * 100;
    slider.value = String(Math.round(pct));
    setSplit(pct);
  }
  viewport.addEventListener('pointerdown', (e) => { dragging = true; handlePointer(e); });
  window.addEventListener('pointerup', () => { dragging = false; });
  window.addEventListener('pointermove', (e) => { if (dragging) handlePointer(e); });

  panel.appendChild(labels);
  panel.appendChild(viewport);
  panel.appendChild(slider);

  // Revoke the after URL when the panel is removed from the DOM
  const obs = new MutationObserver(() => {
    if (!document.contains(panel)) {
      URL.revokeObjectURL(afterUrl);
      obs.disconnect();
    }
  });
  obs.observe(document.body, { childList: true, subtree: true });

  return panel;
}
